import React, { SyntheticEvent, useMemo } from 'react';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { Box, CircularProgress, List, ListItem, Tooltip, Typography } from '@material-ui/core';
import { ChevronRight, Comment, LabelImportantRounded } from '@material-ui/icons';
import { BulletinManagement } from './__generated__/BulletinManagement';
import { audienceNames, bulletinAuthor, bulletinDateLabel, bulletinPlainText } from './bulletin-model';
import {
  AUDIENCE_WIDTH,
  AUTHOR_WIDTH,
  DATE_WIDTH,
  PIN_WIDTH,
  PIN_WIDTH_COMPACT,
  REPLIES_WIDTH,
  useBulletinRowStyles,
} from './bulletinStyles';

interface BulletinListRowProps {
  bulletin: BulletinManagement;
  selected: boolean;
  panelOpen: boolean;
  canUserAddEdit: boolean;
  isPinning: boolean;
  onOpen: (bulletin: BulletinManagement) => void;
  onTogglePinned: (bulletin: BulletinManagement) => void;
}

const Audience = ({ bulletin }: { bulletin: BulletinManagement }) => {
  const names = audienceNames(bulletin);

  if (names.length === 0) {
    return (
      <Typography noWrap style={{ fontSize: 12.5, color: '#9E9E9E' }}>
        Everyone
      </Typography>
    );
  }

  if (names.length === 1) {
    return (
      <Typography noWrap style={{ fontSize: 12.5, color: '#545454' }}>
        {names[0]}
      </Typography>
    );
  }

  return (
    <Tooltip
      arrow
      title={
        <List dense disablePadding>
          {names.map((name) => (
            <ListItem key={name} dense disableGutters>
              {name}
            </ListItem>
          ))}
        </List>
      }
    >
      <Typography noWrap style={{ fontSize: 12.5, color: '#545454' }}>
        {`${names.length} groups`}
      </Typography>
    </Tooltip>
  );
};

export const BulletinListRow = ({
  bulletin,
  selected,
  panelOpen,
  canUserAddEdit,
  isPinning,
  onOpen,
  onTogglePinned,
}: BulletinListRowProps) => {
  const classes = useBulletinRowStyles({ selected });
  const snippet = useMemo(() => bulletinPlainText(bulletin.content), [bulletin.content]);

  const togglePinned = (event: SyntheticEvent) => {
    event.stopPropagation();
    onTogglePinned(bulletin);
  };

  return (
    <Box className={classes.row} role='button' title='Open bulletin' onClick={() => onOpen(bulletin)}>
      <Typography noWrap style={{ width: DATE_WIDTH, flexShrink: 0, fontSize: 12.5, color: '#9E9E9E' }}>
        {bulletinDateLabel(bulletin.dateCreated)}
      </Typography>

      <Box flexGrow={1} minWidth={0}>
        <Typography noWrap style={{ fontSize: 14, fontWeight: 600, color: '#3E3229' }}>
          {bulletin.title}
        </Typography>
        <Typography noWrap style={{ fontSize: 12, color: '#A2A2A2', marginTop: 2 }}>
          {snippet}
        </Typography>
      </Box>

      <Box
        width={panelOpen ? PIN_WIDTH_COMPACT : PIN_WIDTH}
        flexShrink={0}
        display='flex'
        justifyContent='flex-end'
        style={{ paddingRight: 16 }}
      >
        {isPinning && <CircularProgress size={16} />}

        {!isPinning && bulletin.pinned && (
          <TitularTooltip description={canUserAddEdit ? 'Click to unpin' : 'Pinned to the top'}>
            <Box
              className={classes.pinnedChip}
              onClick={canUserAddEdit ? togglePinned : undefined}
              style={{
                cursor: canUserAddEdit ? 'pointer' : 'default',
                ...(panelOpen ? { padding: 3, gap: 0 } : {}),
              }}
            >
              <LabelImportantRounded style={{ fontSize: panelOpen ? 15 : 13 }} />
              {!panelOpen && <Typography style={{ fontSize: 11, fontWeight: 600 }}>Pinned</Typography>}
            </Box>
          </TitularTooltip>
        )}

        {!isPinning && !bulletin.pinned && canUserAddEdit && (
          <TitularTooltip description='Pin to top'>
            <Box className={classes.pinAction} onClick={togglePinned}>
              <LabelImportantRounded style={{ fontSize: 13 }} />
              {!panelOpen && <Typography style={{ fontSize: 11, fontWeight: 600, color: 'inherit' }}>Pin</Typography>}
            </Box>
          </TitularTooltip>
        )}
      </Box>

      <Box width={AUDIENCE_WIDTH} flexShrink={0} minWidth={0}>
        <Audience bulletin={bulletin} />
      </Box>

      {!panelOpen && (
        <Typography noWrap style={{ width: AUTHOR_WIDTH, flexShrink: 0, fontSize: 12.5, color: '#646464' }}>
          {bulletinAuthor(bulletin)}
        </Typography>
      )}

      <Box
        width={REPLIES_WIDTH}
        flexShrink={0}
        display='flex'
        alignItems='center'
        justifyContent='flex-end'
        style={{ gap: 5 }}
      >
        {bulletin.comments && bulletin.comments.length > 0 ? (
          <>
            <Comment style={{ fontSize: 14, color: '#9E9E9E' }} />
            <Typography style={{ fontSize: 12.5, color: '#646464' }}>{bulletin.comments.length}</Typography>
          </>
        ) : (
          <Typography style={{ fontSize: 12.5, color: '#D5D5D5' }}>&ndash;</Typography>
        )}
      </Box>

      <ChevronRight style={{ fontSize: 20, color: '#9E9E9E', flexShrink: 0 }} />
    </Box>
  );
};
